import { Box, Flex } from "@rebass/grid";
import { IPartnership } from "@rentiohq/shared-frontend/dist/redux/expertInquiry/expertInquiry.types";
import {
  ETemplateTag,
  ETemplateType,
  ITemplate,
} from "@rentiohq/shared-frontend/dist/redux/template/template.types";
import { ITemplateDocument } from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.types";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import {
  EBrokerFeature,
  IBroker,
} from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EContractIndexationStatus,
  EContractType,
  EInsuranceExternalStatus,
  ERentioMoveServiceStatus,
  ETemplateDocumentStatus,
  IContract,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EDocumentPackageStatus,
  EDocumentPackageTypes,
  IDocumentPackage,
} from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { EPayoutType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationBrusselsStatus,
  ERegistrationDocumentStatus,
  ERegistrationDocumentType,
  IRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  EBanqupContractStatus,
  ERentDepositProvider,
  ERentDepositStatus,
  IRentDeposit,
} from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import {
  isActiveContract,
  isEndedContract,
} from "@rentiohq/shared-frontend/dist/utils/contract.utils";
import {
  addMonths,
  differenceInYears,
  isBefore,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { canAddPaymentOrders } from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import { hasBrusselsRegistration } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { iHaveRole } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  capitalizeString,
  stringToSnakeCase,
} from "@rentiohq/shared-frontend/dist/utils/string.utils";
import {
  appendQueryParams,
  getRentioMoveUrl,
} from "@rentiohq/shared-frontend/dist/utils/url.utils";
import {
  Appearance,
  ESpacings,
  Grid,
  Icon,
  Lozenge,
  RentioInternalRenderer,
  Stack,
  TextStyle,
  Tooltip,
  renderActions,
} from "@rentiohq/web-shared/dist/components";
import { getStatusVariation } from "@rentiohq/web-shared/dist/utils/rentDeposit";
import {
  getSignerInfo,
  renderSignersTooltip,
} from "utils/documentPackage.utils";
import { rs, ts } from "../../../../../../services";
import { EActionStatus, IChecklistItem } from "./components/ChecklistItem";

export const STATUS_IS_ENDING_WHEN_IN_MONTHS = 9;

const handleChangeActionStatus = (params: {
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];
  key:
    | "manuallyCompletedTemplateDocumentContract"
    | "manuallyCompletedDocumentSigning"
    | "manuallyCompletedRentDeposit"
    | "manuallyCompletedEndRentDeposit"
    | "manuallyCompletedLocationDescription"
    | "manuallyCompletedEndPropertyInspectionReport"
    | "manuallyCompletedPaymentRent"
    | "manuallyCompletedEndPaymentRent"
    | "manuallyCompletedMove"
    | "manuallyCompletedEndMove"
    | "manuallyCompletedInsurance"
    | "manuallyCompletedRegistration";
  entityKey: string;
  shouldCheck: boolean;
}) => {
  const { updateContract, key, entityKey, shouldCheck } = params;

  const entity = getLocalizedText(`system.${entityKey}`).toLowerCase();

  if (shouldCheck) {
    confirm({
      title: capitalizeString(
        getLocalizedText("contract_action.manual.check.confirm.title", {
          entity,
        }),
      ),
      info: capitalizeString(
        getLocalizedText("contract_action.manual.check.confirm.info", {
          entity,
        }),
      ),
      inputValueProps: {
        type: "string",
        initialValue: getLocalizedText("contract_action.manual"),
        extraInfoValue: getLocalizedText(
          `contract_action.manual.check_example.${entityKey}`,
        ),
      },
      primaryActions: [
        {
          title: getLocalizedText("system.check"),
          onPress: (params?: { inputValue?: string | Date }) => {
            const { inputValue = "" } = params || {};

            if (typeof inputValue !== "string") {
              return;
            }

            let reason = inputValue;
            if (
              reason.length === 0 ||
              reason === getLocalizedText("contract_action.manual")
            ) {
              reason = "contract_action.manual";
            }

            updateContract({
              data: {
                [key]: reason,
              },
              customSuccessMessage: capitalizeString(
                getLocalizedText(
                  "contract_action.manual.check.toast.success.title",
                  { entity, reason: getLocalizedText(reason) },
                ),
              ),
              customFailureMessage: capitalizeString(
                getLocalizedText(
                  "contract_action.manual.check.toast.failure.title",
                  { entity },
                ),
              ),
            });
          },
        },
      ],
    });

    return;
  }

  confirm({
    title: capitalizeString(
      getLocalizedText("contract_action.manual.uncheck.confirm.title", {
        entity,
      }),
    ),
    info: capitalizeString(
      getLocalizedText("contract_action.manual.uncheck.confirm.info", {
        entity,
      }),
    ),
    primaryActions: [
      {
        title: getLocalizedText("system.uncheck"),
        onPress: () => {
          updateContract({
            data: {
              [key]: null,
            },
            customSuccessMessage: capitalizeString(
              getLocalizedText(
                "contract_action.manual.uncheck.toast.success.title",
                { entity },
              ),
            ),
            customFailureMessage: capitalizeString(
              getLocalizedText(
                "contract_action.manual.uncheck.toast.failure.title",
                { entity },
              ),
            ),
          });
        },
      },
    ],
  });

  return;
};

export const getIndexationMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Callbacks
  onIndexContract: () => void;

  // Config
  internalModeEnabled: boolean;
}): IChecklistItem | undefined => {
  const {
    property,
    contract,

    // Callbacks
    onIndexContract,

    // Config
    internalModeEnabled,
  } = params;

  if (isEndedContract(contract)) {
    return;
  }

  if (contract.indexationStatus === EContractIndexationStatus.NotReady) {
    return;
  }

  if (!iHaveRole(property, EPropertyMemberTypes.IndexationManager)) {
    if (internalModeEnabled) {
      return {
        actionStatus: EActionStatus.Incomplete,
        title: "",
        content: (
          <RentioInternalRenderer>
            Indexation is possible, but no indexation role
          </RentioInternalRenderer>
        ),
        rightContent: renderActions({
          content: "View indexation",
          onClick: onIndexContract,
          appearance: "link",
          color: "green",
          size: "small",
        }),
      };
    }
    return;
  }

  const common = {
    title: ts.propertyDashboardChecklistIndexContractHeading(),
    onClick: onIndexContract,
  };

  let showActionStatus = false;
  let actionStatus = EActionStatus.Incomplete;
  let buttonAppearance: Appearance | undefined;
  switch (contract.indexationStatus) {
    case EContractIndexationStatus.OwnerAsked:
      showActionStatus = true;
      actionStatus = EActionStatus.Incomplete;
      buttonAppearance = "link";
      break;

    case EContractIndexationStatus.OwnerApproved:
      showActionStatus = true;
      actionStatus = EActionStatus.UrgentActionRentio;
      break;

    case EContractIndexationStatus.OwnerDeclined:
      showActionStatus = true;
      actionStatus = EActionStatus.UrgentActionRentio;
      break;

    case EContractIndexationStatus.Muted:
      showActionStatus = true;
      actionStatus = EActionStatus.Incomplete;
      buttonAppearance = "link";
      break;

    case EContractIndexationStatus.Ready:
    default:
      break;
  }

  return {
    ...common,
    actionStatus,
    actionStatusText: showActionStatus
      ? getLocalizedText(
          `property.dashboard.checklist.index_contract.${contract.indexationStatus}.heading`.toLowerCase(),
        )
      : undefined,
    content: (
      <div>
        {getLocalizedText(
          `property.dashboard.checklist.index_contract.${contract.indexationStatus}.content`.toLowerCase(),
        )}
      </div>
    ),
    rightContent: renderActions({
      content: `${getLocalizedText(
        `property.dashboard.checklist.index_contract.${contract.indexationStatus}.action`.toLowerCase(),
      )} ›`,
      onClick: onIndexContract,
      appearance: buttonAppearance,
      color: "green",
      size: "small",
    }),
  };
};

export const getTemplateDocumentContractMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Callbacks
  onCreateContractFromTemplateDocument: () => void;
  onShowDocument: (documentId: string) => void;
  onShowTemplateDocument: (templateDocumentId: string) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  isBroker: boolean;
  templates?: ITemplate[];
  isFetchingTemplates: boolean;
  contractTemplateDocuments?: ITemplateDocument[];
  isFetchingContractTemplateDocuments: boolean;
}): IChecklistItem | undefined => {
  const {
    property,
    contract,

    // Callbacks
    onCreateContractFromTemplateDocument,
    onShowDocument,
    onShowTemplateDocument,

    // Actions
    updateContract,

    // Config
    isBroker,
    templates,
    isFetchingTemplates,
    contractTemplateDocuments,
    isFetchingContractTemplateDocuments,
  } = params;

  const { manuallyCompletedTemplateDocumentContract } = contract;

  const contractIsEnded = isEndedContract(contract);

  const availableTemplatesForContract = getAvailableTemplatesForType({
    type: "contract",
    isBroker,
    templates,
    contract,
    property,
  });

  const title = getLocalizedText(
    "property.dashboard.checklist.template_document_contract.heading",
  );
  const entityKey = "template_document_contract";

  if (manuallyCompletedTemplateDocumentContract) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedTemplateDocumentContract",
          entityKey,
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(
        manuallyCompletedTemplateDocumentContract,
      ),
      title,
      content: (
        <div>
          {getLocalizedText(
            "property.dashboard.checklist.template_document_contract.empty_content",
          )}
        </div>
      ),
      rightContent: undefined,
    };
  }

  // No available templates & no existing template documents
  const templateDocument = contractTemplateDocuments?.[0];
  if (
    availableTemplatesForContract &&
    availableTemplatesForContract.length === 0 &&
    !templateDocument
  ) {
    return;
  }

  const showLoading =
    (!availableTemplatesForContract && isFetchingTemplates) ||
    (!contractTemplateDocuments && isFetchingContractTemplateDocuments);
  if (showLoading) {
    return {
      actionStatus: EActionStatus.Loading,
      title,
      content: null, // <div>&nbsp;</div>,
    };
  }

  // Template document
  if (templateDocument) {
    const { status, documentId } = templateDocument;

    let showActions = true;
    let actionStatus = EActionStatus.Incomplete;
    switch (status) {
      case ETemplateDocumentStatus.Generating:
        actionStatus = EActionStatus.PendingRentio;
        break;

      case ETemplateDocumentStatus.Generated:
        actionStatus = EActionStatus.CompletedInRentio;
        break;

      // TODO: Failed
      case ETemplateDocumentStatus.Failed:
      case ETemplateDocumentStatus.New:
      default:
        break;
    }

    const onClick = () => {
      switch (status) {
        case ETemplateDocumentStatus.Generated:
        case ETemplateDocumentStatus.Generating:
          if (documentId) {
            onShowDocument(documentId);
          }

          break;

        case ETemplateDocumentStatus.Failed:
        case ETemplateDocumentStatus.New:
        default: {
          onShowTemplateDocument(templateDocument.id);

          break;
        }
      }
    };

    return {
      actionStatus,
      title,
      content: (
        <div>
          {getLocalizedText(
            `property.dashboard.checklist.template_document_contract.status.${status}`.toLowerCase(),
          )}
        </div>
      ),
      onClick,
      rightContent:
        showActions &&
        renderActions({
          content: `${getLocalizedText(
            `property.dashboard.checklist.template_document_contract.info_action.${status}`.toLowerCase(),
          )} ›`,
          onClick,
          appearance: "link",
        }),
    };
  }

  // Empty state
  return {
    actionStatus: EActionStatus.Incomplete,
    title,
    content: (
      <div>
        {getLocalizedText(
          "property.dashboard.checklist.template_document_contract.content",
        )}
      </div>
    ),
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedTemplateDocumentContract",
        entityKey,
        shouldCheck: true,
      });
    },
    onClick: contractIsEnded ? undefined : onCreateContractFromTemplateDocument,
    rightContent: contractIsEnded
      ? undefined
      : renderActions({
          content: `${getLocalizedText(
            "property.dashboard.checklist.template_document_contract.empty_action",
          )} ›`,
          onClick: onCreateContractFromTemplateDocument,
          color: "green",
          size: "small",
        }),
  };
};

const renderDocumentPackageInfo = (params: {
  documentPackage: IDocumentPackage;
}) => {
  const { documentPackage } = params;

  if (!documentPackage) {
    return null;
  }

  if (
    [EDocumentPackageStatus.Pending, EDocumentPackageStatus.Finished].includes(
      documentPackage.status!,
    )
  ) {
    const { signerCount = 0, signedCount = 0 } = documentPackage
      ? getSignerInfo(documentPackage)
      : {};

    return (
      <Grid spacing="tight" alignItems="center" justifyContent="flex-end">
        <Grid.Item>
          <Icon source="contentPenWrite" size="small" />
        </Grid.Item>
        <Grid.Item>
          <Tooltip tooltipContent={renderSignersTooltip(documentPackage)}>
            <TextStyle
              variation={[
                "code",
                signedCount >= signerCount ? "positive" : "negative",
              ]}
            >
              {signedCount}/{signerCount}
            </TextStyle>
          </Tooltip>
        </Grid.Item>
      </Grid>
    );
  }

  return ts.documentPackagesStatus(documentPackage.status);
};

export const getDocumentPackageMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Callbacks
  onShowDocumentPackage: (documentPackageId: string) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  documentPackage?: IDocumentPackage;
  isFetchingDocumentPackage: boolean;
}): IChecklistItem | undefined => {
  const {
    property,
    contract,

    // Callbacks
    onShowDocumentPackage,

    // Actions
    updateContract,

    // Config
    documentPackage,
    isFetchingDocumentPackage,
  } = params;

  const { manuallyCompletedDocumentSigning } = contract;
  const contractIsEnded = isEndedContract(contract);

  if (!contract.contractType || contract.contractType !== EContractType.Basic) {
    return;
  }

  if (manuallyCompletedDocumentSigning) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedDocumentSigning",
          entityKey: "contract_signing",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedDocumentSigning),
      title: ts.propertyDashboardChecklistSignContractHeading(),
      content: (
        <div>{ts.propertyDashboardChecklistSignContractEmptyContent()}</div>
      ),
      rightContent: undefined,
    };
  }

  if (!documentPackage && isFetchingDocumentPackage) {
    return {
      actionStatus: EActionStatus.Loading,
      title: ts.propertyDashboardChecklistSignContractHeading(),
      content: null,
    };
  }

  if (documentPackage) {
    const actionStatus =
      documentPackage.status === EDocumentPackageStatus.Finished
        ? EActionStatus.CompletedInRentio
        : EActionStatus.PendingRentio;

    return {
      actionStatus,
      title: ts.propertyDashboardChecklistSignContractHeading(),
      content: <div>{ts.propertyDashboardChecklistSignContractContent()}</div>,
      onClick: () => {
        onShowDocumentPackage(documentPackage.id);
      },
      rightContent: (
        <Flex>
          {renderDocumentPackageInfo({ documentPackage })}
          <Box ml={ESpacings.tight}>
            {renderActions({
              content: `${ts.propertyDashboardChecklistViewDocumentPackageAction()} ›`,
              onClick: () => {
                onShowDocumentPackage(documentPackage.id);
              },
              appearance: "link",
            })}
          </Box>
        </Flex>
      ),
    };
  }

  const link = appendQueryParams({
    path: "/forms/document-package",
    queryParams: {
      propertyId: property.id,
      contractId: contract.id,
      documentPackageType: EDocumentPackageTypes.Contract,
    },
  });

  return {
    actionStatus: EActionStatus.Incomplete,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedDocumentSigning",
        entityKey: "contract_signing",
        shouldCheck: true,
      });
    },
    title: ts.propertyDashboardChecklistSignContractHeading(),
    content: (
      <div>{ts.propertyDashboardChecklistSignContractEmptyContent()}</div>
    ),
    link: contractIsEnded ? undefined : link,
    rightContent: contractIsEnded
      ? undefined
      : renderActions({
          content: `${ts.propertyDashboardChecklistSignContractAction()} ›`,
          url: link,
          // onClick: handlePrepareContract,
          color: "green",
          size: "small",
        }),
  };
};

export const getRentDepositMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Callbacks
  onShowRentDeposit: (rentDepositId: number) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  rentDeposit?: IRentDeposit;
  isFetchingRentDeposit: boolean;
}): IChecklistItem | undefined => {
  const {
    property,
    contract,

    // Callbacks
    onShowRentDeposit,

    // Actions
    updateContract,

    // Config
    rentDeposit,
    isFetchingRentDeposit,
  } = params;

  const { manuallyCompletedRentDeposit } = contract;
  const contractIsEnded = isEndedContract(contract);

  if (contract.contractType !== EContractType.Basic) {
    return;
  }

  if (manuallyCompletedRentDeposit) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedRentDeposit",
          entityKey: "rent_deposit",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedRentDeposit),
      title: ts.propertyDashboardChecklistRentDepositHeading(),
      content: (
        <div>{ts.propertyDashboardChecklistRentDepositEmptyContent()}</div>
      ),
      rightContent: undefined,
    };
  }

  if (!rentDeposit && isFetchingRentDeposit) {
    return {
      actionStatus: EActionStatus.Loading,
      title: ts.propertyDashboardChecklistRentDepositHeading(),
      content: null, // <div>&nbsp;</div>,
    };
  }

  if (!rentDeposit || rentDeposit.status === ERentDepositStatus.Cancelled) {
    return {
      actionStatus: EActionStatus.Incomplete,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedRentDeposit",
          entityKey: "rent_deposit",
          shouldCheck: true,
        });
      },
      title: ts.propertyDashboardChecklistRentDepositHeading(),
      content: (
        <div>{ts.propertyDashboardChecklistRentDepositEmptyContent()}</div>
      ),
      link: contractIsEnded
        ? undefined
        : rs.createRentDepositRoute(property.id, contract.id),
      rightContent: contractIsEnded
        ? undefined
        : renderActions({
            content: `${ts.propertyDashboardChecklistRentDepositEmptyAction()} ›`,
            url: rs.createRentDepositRoute(property.id, contract.id),
            color: "green",
            size: "small",
          }),
    };
  }

  const isMigrationCase =
    rentDeposit.provider === ERentDepositProvider.Migrated;

  const rentDepositOpeningCompletedStatuses = [
    ERentDepositStatus.Established,
    ERentDepositStatus.ReleaseNew,
    ERentDepositStatus.WaitingForIBAN,
    ERentDepositStatus.WaitingForValidations,
    ERentDepositStatus.ReleaseToSign,
    ERentDepositStatus.ReleaseSigned,
    ERentDepositStatus.ManualVerification,
    ERentDepositStatus.ManualVerificationJudicial,
    ERentDepositStatus.PayingOut,
    ERentDepositStatus.Released,
    ERentDepositStatus.BanqupPendingPayment,
  ];

  const rentDepositOpeningPendingStatuses = [
    ERentDepositStatus.New,
    ERentDepositStatus.OpenToSign,
    ERentDepositStatus.OpenSigned,
    ERentDepositStatus.PayingIn,
    ERentDepositStatus.PayingInPartiallySigned,
    ERentDepositStatus.PaidInPartiallySigned,
    ERentDepositStatus.WaitingForVerificationByBanqup,
    ERentDepositStatus.VerifyingRentDepositHolder,
    ERentDepositStatus.RequestingBanqupAccount,
  ];

  let actionStatus = EActionStatus.PendingRentio;

  const isEstablished =
    isMigrationCase ||
    rentDepositOpeningCompletedStatuses.includes(rentDeposit.status);

  const showStatusTag =
    !isMigrationCase &&
    rentDeposit.provider === ERentDepositProvider.Banqup &&
    rentDepositOpeningPendingStatuses.includes(rentDeposit.status);

  if (isEstablished) {
    actionStatus = EActionStatus.CompletedInRentio;
  }

  const getContent = () => {
    if (isEstablished) {
      if (rentDeposit.status === ERentDepositStatus.BanqupPendingPayment) {
        const paymentMethod = getPaymentMethodFromRentDepositPo(rentDeposit);
        return getLocalizedText(
          `follow_up.rent_deposits.filter.status.${rentDeposit.status.toLowerCase()}.label`,
          {
            paymentMethod: paymentMethod
              ? getLocalizedText(`payment_request.method.${paymentMethod}`)
              : getLocalizedText("system.unknown"),
          },
        );
      }

      return getLocalizedText(
        "property.dashboard.checklist.rent_deposit.content.established",
        {
          value: formatCurrency(rentDeposit.amount),
        },
      );
    }

    return getLocalizedText(
      "property.dashboard.checklist.rent_deposit.content",
    );
  };

  const content = (
    <Stack alignment="center" spacing="tight">
      <Box>{getContent()}</Box>
      {showStatusTag && (
        <Box>
          <Lozenge
            isBold
            key={`${rentDeposit.id}-payment-status`}
            appearance={getStatusVariation(rentDeposit.status)}
          >
            {ts.followUpRentDepositsFilterStatusLabel({
              extra: { key: rentDeposit.status },
            })}
          </Lozenge>
        </Box>
      )}
    </Stack>
  );

  return {
    actionStatus,
    title: ts.propertyDashboardChecklistRentDepositHeading(),
    content,
    onClick: () => onShowRentDeposit(rentDeposit.id),
    rightContent: renderActions({
      content: `${ts.propertyDashboardChecklistRentDepositAction()} ›`,
      appearance: "link",
      onClick: () => onShowRentDeposit(rentDeposit.id),
    }),
  };
};

export const getPaymentOrderMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  isBroker: boolean;
  paymentOrdersCount?: number;
  isFetchingPaymentOrdersCount: boolean;
}): IChecklistItem | undefined => {
  const {
    property,
    contract,

    // Actions
    updateContract,

    // Config
    isBroker,
    paymentOrdersCount = 0,
    isFetchingPaymentOrdersCount,
  } = params;

  const { manuallyCompletedPaymentRent } = contract;
  const contractIsEnded = isEndedContract(contract);

  if (contract.contractType !== EContractType.Basic) {
    return;
  }

  if (manuallyCompletedPaymentRent) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedPaymentRent",
          entityKey: "payment_orders",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedPaymentRent),
      title: ts.propertyDashboardChecklistPaymentOrderHeading(),
      content: (
        <div>{ts.propertyDashboardChecklistPaymentOrderEmptyContent()}</div>
      ),
      rightContent: undefined,
    };
  }

  if (paymentOrdersCount === 0 && isFetchingPaymentOrdersCount) {
    return {
      actionStatus: EActionStatus.Loading,
      title: ts.propertyDashboardChecklistPaymentOrderHeading(),
      content: null, // <div>&nbsp;</div>,
    };
  }

  // Don't show if payments are already added
  if (paymentOrdersCount > 0) {
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      title: ts.propertyDashboardChecklistPaymentOrderHeading(),
      content: (
        <div>{ts.propertyDashboardChecklistPaymentOrderCreatedContent()}</div>
      ),
      link: `/properties/${property.id}/payments`,
      rightContent: renderActions({
        content: `${getLocalizedText("system.view")} ›`,
        appearance: "link",
        url: `/properties/${property.id}/payments`,
      }),
    };
  }

  if (
    !canAddPaymentOrders({
      property,
      isBroker,
    })
  ) {
    return undefined;
  }

  return {
    actionStatus: EActionStatus.Incomplete,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedPaymentRent",
        entityKey: "payment_orders",
        shouldCheck: true,
      });
    },
    title: ts.propertyDashboardChecklistPaymentOrderHeading(),
    content: (
      <div>{ts.propertyDashboardChecklistPaymentOrderEmptyContent()}</div>
    ),
    link: contractIsEnded
      ? undefined
      : `/properties/${property.id}/payments/add?contractId=${contract.id}`,
    rightContent: contractIsEnded
      ? undefined
      : renderActions({
          content: `${ts.propertyDashboardChecklistPaymentOrderEmptyAction()} ›`,
          url: `/properties/${property.id}/payments/add?contractId=${contract.id}`,
          color: "green",
          size: "small",
        }),
  };
};

export const getPropertyInspectionReportMessage = (params: {
  property: IProperty;
  contract: IContract;
  isBroker: boolean;
  broker?: IBroker;

  // Callbacks
  onOrderPropertyInspectionReport: () => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  partnerships?: IPartnership[];
}): IChecklistItem | undefined => {
  const {
    // property,
    contract,
    isBroker,
    broker,

    // Callbacks
    onOrderPropertyInspectionReport,

    // Actions
    updateContract,

    // Config
    partnerships,
  } = params;

  const { manuallyCompletedLocationDescription } = contract;
  const contractIsEnded = isEndedContract(contract);
  const contractIsActive = isActiveContract(contract);

  const isContractStarting =
    isBefore(new Date(), addMonths(contract.startDate, 1)) &&
    !contract.locationDescriptionInRequestedAt;

  const canStartPropertyInspection = isBroker
    ? broker?.features.includes(EBrokerFeature.PropertyInspectionReport)
    : true;
  const hideAction = !contractIsEnded && !canStartPropertyInspection;

  const alreadyOrderedOnClick = hideAction
    ? undefined
    : onOrderPropertyInspectionReport;
  const alreadyOrderedRightContent = hideAction
    ? undefined
    : renderActions({
        content: `${ts.contractCardChecklistLocationDescriptionAgainAction()} ›`,
        onClick: onOrderPropertyInspectionReport,
        color: "green",
        appearance: "link",
      });

  if (manuallyCompletedLocationDescription) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedLocationDescription",
          entityKey: "location_description",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedLocationDescription),
      title: ts.contractCardChecklistLocationDescriptionHeading(),
      content: (
        <div>
          {getLocalizedText(
            "contract_card.checklist.location_description.manually",
          )}
        </div>
      ),
      rightContent: undefined,
    };
  }

  if (
    contract.locationDescriptionInRequestedAt &&
    contract.locationDescriptionOutRequestedAt
  ) {
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      title: ts.contractCardChecklistLocationDescriptionHeading(),
      content: (
        <div>
          {ts.contractCardChecklistLocationDescriptionBothContent({
            values: {
              date: formatDate(contract.locationDescriptionOutRequestedAt!),
            },
          })}
        </div>
      ),
      onClick: alreadyOrderedOnClick,
      rightContent: alreadyOrderedRightContent,
    };
  }

  // if (contract.locationDescriptionOutRequestedAt) {
  //   return {
  //     actionStatus: EActionStatus.CompletedInRentio,
  //     title: ts.contractCardChecklistLocationDescriptionHeading(),
  //     content: (
  //       <div>
  //         {ts.contractCardChecklistLocationDescriptionOutContent({
  //           values: {
  //             date: formatDate(contract.locationDescriptionOutRequestedAt!),
  //           },
  //         })}
  //       </div>
  //     ),
  //     onClick: alreadyOrderedOnClick,
  //     rightContent: alreadyOrderedRightContent,
  //   };
  // }

  if (contract.locationDescriptionInRequestedAt) {
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      title: ts.contractCardChecklistLocationDescriptionHeading(),
      content: (
        <div>
          {ts.contractCardChecklistLocationDescriptionInContent({
            values: {
              date: formatDate(contract.locationDescriptionInRequestedAt!),
            },
          })}
        </div>
      ),
      onClick: alreadyOrderedOnClick,
      rightContent: alreadyOrderedRightContent,
    };
  }

  // Only hide if feature enabled & there are no partnerships
  if (!partnerships) {
    return undefined;
  }

  if (isContractStarting || contractIsActive) {
    const title = ts.contractCardChecklistLocationDescriptionHeading();

    return {
      actionStatus: EActionStatus.Incomplete,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedLocationDescription",
          entityKey: "location_description",
          shouldCheck: true,
        });
      },
      title,
      content: (
        <div>
          {getLocalizedText(
            "contract_card.checklist.location_description.empty_content",
          )}
        </div>
      ),
      onClick: hideAction ? undefined : onOrderPropertyInspectionReport,
      rightContent: hideAction
        ? undefined
        : renderActions({
            content: `${ts.contractCardChecklistLocationDescriptionEmptyAction()} ›`,
            onClick: onOrderPropertyInspectionReport,
            color: "green",
            size: "small",
          }),
    };
  }
};

export const getInsuranceMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Callbacks
  onRequestInsurance: () => void;
  onShowInsurance: (insuranceContractId: string) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  isBroker: boolean;
  hasInsuranceForBroker: boolean;
  internalModeEnabled: boolean;
}): IChecklistItem | undefined => {
  const {
    contract,

    // Callbacks
    onRequestInsurance,
    onShowInsurance,

    // Actions
    updateContract,

    // Config
    isBroker,
    hasInsuranceForBroker,
    internalModeEnabled,
  } = params;

  const { manuallyCompletedInsurance } = contract;
  const contractIsEnded = isEndedContract(contract);

  if (isBroker && !hasInsuranceForBroker && !internalModeEnabled) {
    return {
      actionStatus: manuallyCompletedInsurance
        ? EActionStatus.CompletedManually
        : EActionStatus.Incomplete,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedInsurance",
          entityKey: "insurance",
          shouldCheck: !manuallyCompletedInsurance,
        });
      },
      actionStatusText: manuallyCompletedInsurance
        ? getLocalizedText(manuallyCompletedInsurance)
        : undefined,
      title: getLocalizedText(
        "contract_card.checklist.insurance.heading.manual",
      ),
      content: getLocalizedText(
        "contract_card.checklist.insurance.content.manual",
      ),
      rightContent: undefined,
    };
  }

  let title = getLocalizedText("contract_card.checklist.insurance.heading");

  if (manuallyCompletedInsurance) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedInsurance",
          entityKey: "insurance",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedInsurance),
      title,
      content: getLocalizedText(
        "contract_card.checklist.insurance.content.none",
      ),
      rightContent: undefined,
    };
  }

  const { insuranceExternalStatus, insuranceLeadCompletedDate } = contract;

  let content = getLocalizedText(
    `contract_card.checklist.insurance.content.${insuranceExternalStatus}`.toLowerCase(),
    {
      date: insuranceLeadCompletedDate
        ? formatDate(insuranceLeadCompletedDate)
        : "",
    },
  );

  let actionStatus = EActionStatus.Incomplete;
  let rightContent = null;
  let onClick = undefined;

  switch (insuranceExternalStatus) {
    case EInsuranceExternalStatus.None: {
      actionStatus = EActionStatus.Incomplete;

      onClick = contractIsEnded ? undefined : onRequestInsurance;

      rightContent = contractIsEnded
        ? undefined
        : renderActions({
            content: `${getLocalizedText(
              "contract_card.checklist.insurance.list_action",
            )} ›`,
            onClick,
            color: "green",
            size: "small",
          });

      break;
    }

    case EInsuranceExternalStatus.Created:
    case EInsuranceExternalStatus.Pending:
    case EInsuranceExternalStatus.Failed: {
      actionStatus = EActionStatus.PendingRentio;

      onClick = () => {
        onShowInsurance(contract.id);
      };

      rightContent = renderActions({
        content: `${getLocalizedText(
          "contract_card.checklist.insurance.list_action.detail",
        )} ›`,
        onClick,
        appearance: "link",
      });

      break;
    }

    case EInsuranceExternalStatus.Completed:
      actionStatus = EActionStatus.CompletedInRentio;

      onClick = () => {
        onShowInsurance(contract.id);
      };

      rightContent = renderActions({
        content: `${getLocalizedText(
          "contract_card.checklist.insurance.list_action.detail",
        )} ›`,
        onClick,
        appearance: "link",
      });
      break;

    default:
      break;
  }

  return {
    actionStatus,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedInsurance",
        entityKey: "insurance",
        shouldCheck: true,
      });
    },
    title,
    content,
    onClick,
    rightContent,
  };
};

export const getRentioMoveServiceMessage = (params: {
  property: IProperty;
  contract: IContract;
  hasRentioMoveServiceForBroker: boolean;
  // Callbacks
  onShowMoveRequest: (moveRequestId: number) => void;
  onCreateRentioMoveRequest: () => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  isBroker: boolean;
  internalModeEnabled: boolean;
}): IChecklistItem | undefined => {
  const {
    contract,

    // Callbacks
    onCreateRentioMoveRequest,

    // Actions
    updateContract,
    // Config
    isBroker,
    hasRentioMoveServiceForBroker,
    internalModeEnabled,
  } = params;
  const { rentioMoveStatus, manuallyCompletedMove } = contract;

  const contractIsEnded = isEndedContract(contract);

  if (isBroker && !hasRentioMoveServiceForBroker && !internalModeEnabled) {
    return {
      actionStatus: manuallyCompletedMove
        ? EActionStatus.CompletedManually
        : EActionStatus.Incomplete,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedMove",
          entityKey: "rentio_move_service",
          shouldCheck: !manuallyCompletedMove,
        });
      },
      actionStatusText: manuallyCompletedMove
        ? getLocalizedText(manuallyCompletedMove)
        : undefined,
      title: getLocalizedText(
        "contract_card.checklist.rentio_move_service.heading.manual",
      ),
      content: getLocalizedText(
        "contract_card.checklist.rentio_move_service.content.manual",
      ),
      rightContent: undefined,
    };
  }

  let title = getLocalizedText(
    "contract_card.checklist.rentio_move_service.heading",
  );
  let content = getLocalizedText(
    `contract_card.checklist.rentio_move_service.content.${
      rentioMoveStatus || ERentioMoveServiceStatus.None
    }`.toLowerCase(),
  );

  if (manuallyCompletedMove) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedMove",
          entityKey: "rentio_move_service",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedMove),
      title,
      content,
      rightContent: undefined,
    };
  }

  if (contract.rentioMoveStatus === ERentioMoveServiceStatus.Processing) {
    const rentioMoveDashboardLink = getRentioMoveUrl(contract.id);
    return {
      actionStatus: EActionStatus.PendingRentio,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedMove",
          entityKey: "rentio_move_service",
          shouldCheck: true,
        });
      },
      title,
      content,
      onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
      rightContent: renderActions({
        content: `${getLocalizedText(
          "contract_card.checklist.rentio_move_service.list_action.detail",
        )} ›`,
        onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
        appearance: "link",
      }),
    };
  }

  if (contract.rentioMoveStatus === ERentioMoveServiceStatus.Sent) {
    const rentioMoveDashboardLink = getRentioMoveUrl(contract.id);
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedMove",
          entityKey: "rentio_move_service",
          shouldCheck: true,
        });
      },
      title,
      content,
      onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
      rightContent: renderActions({
        content: `${getLocalizedText(
          "contract_card.checklist.rentio_move_service.list_action.detail",
        )} ›`,
        onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
        appearance: "link",
      }),
    };
  }

  return {
    actionStatus: EActionStatus.Incomplete,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedMove",
        entityKey: "rentio_move_service",
        shouldCheck: true,
      });
    },
    title,
    content,
    onClick: contractIsEnded ? undefined : onCreateRentioMoveRequest,
    rightContent: contractIsEnded
      ? undefined
      : renderActions({
          content: `${getLocalizedText(
            "contract_card.checklist.rentio_move_service.list_action.start",
          )} ›`,
          onClick: onCreateRentioMoveRequest,
          color: "green",
          size: "small",
        }),
  };
};

export const getRegistrationMessage = (params: {
  property: IProperty;
  contract: IContract;
  registration?: IRegistration;

  // Callbacks
  onShowRentRegistration: (registrationId: number) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];
}): IChecklistItem | undefined => {
  const {
    property,
    contract,
    registration,

    // Callbacks
    onShowRentRegistration,

    // Actions
    updateContract,
  } = params;

  const { manuallyCompletedRegistration } = contract;
  const contractIsEnded = isEndedContract(contract);

  const { rentRegistrationId } = contract;

  const rentRegistrationDocuments = registration?.documents;

  const contractDocument = rentRegistrationDocuments?.find(
    document => document?.documentType === ERegistrationDocumentType.Contract,
  );

  const propertyInspectionDocument = rentRegistrationDocuments?.find(
    document =>
      document?.documentType ===
      ERegistrationDocumentType.PropertyInspectionReport,
  );

  const addendumDocument = rentRegistrationDocuments?.find(
    document => document?.documentType === ERegistrationDocumentType.Addendum,
  );

  const contractDocumentStatus =
    contractDocument?.status || ERegistrationDocumentStatus.None;
  const propertyInspectionDocumentStatus =
    propertyInspectionDocument?.status || ERegistrationDocumentStatus.None;

  const documentStatuses = [
    contractDocumentStatus,
    propertyInspectionDocumentStatus,
  ];

  if (addendumDocument) {
    documentStatuses.push(addendumDocument?.status);
  }

  let actionStatus = EActionStatus.Incomplete;

  if (
    documentStatuses.every(
      status =>
        status &&
        [
          ERegistrationDocumentStatus.Registered,
          ERegistrationDocumentStatus.NotApplicable,
          ERegistrationDocumentStatus.RegisteredManually,
        ].includes(status),
    )
  ) {
    actionStatus = EActionStatus.CompletedInRentio;
  }

  if (
    documentStatuses.some(
      status =>
        status &&
        [
          ERegistrationDocumentStatus.Failed,
          ERegistrationDocumentStatus.Rejected,
          ERegistrationDocumentStatus.Registering,
          ERegistrationDocumentStatus.NeedPayment,
        ].includes(status),
    )
  ) {
    actionStatus = EActionStatus.PendingRentio;
  }

  const hasSomeRegistered = documentStatuses.some(status =>
    [
      ERegistrationDocumentStatus.Registered,
      ERegistrationDocumentStatus.NotApplicable,
      ERegistrationDocumentStatus.RegisteredManually,
    ].includes(status),
  );

  const hasSomeNotRegistered = documentStatuses.some(
    status =>
      ![
        ERegistrationDocumentStatus.Registered,
        ERegistrationDocumentStatus.NotApplicable,
        ERegistrationDocumentStatus.RegisteredManually,
      ].includes(status),
  );

  if (hasSomeRegistered && hasSomeNotRegistered) {
    actionStatus = EActionStatus.PendingRentio;
  }

  let title = getLocalizedText("contract_card.checklist.registration.heading");

  if (manuallyCompletedRegistration) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedRegistration",
          entityKey: "registration",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedRegistration),
      title,
      content: getLocalizedText(
        "contract_card.checklist.registration.content.empty",
      ),
      rightContent: undefined,
    };
  }

  let content = getLocalizedText(
    `contract_card.checklist.registration.content.${
      contractDocumentStatus === ERegistrationDocumentStatus.None
        ? "empty"
        : "other"
    }`,
    {
      rentContractStatus: getLocalizedText(
        `registration.status.${stringToSnakeCase(contractDocumentStatus)}`,
      ).toLowerCase(),
      locationDescriptionStatus: getLocalizedText(
        `registration.status.${stringToSnakeCase(
          propertyInspectionDocumentStatus,
        )}`,
      ).toLowerCase(),
    },
  );

  let rightContent = null;
  let link = undefined;
  let onClick = undefined;

  if (contractDocumentStatus === ERegistrationDocumentStatus.None) {
    link = contractIsEnded
      ? undefined
      : `/properties/${property.id}/contracts/${contract.id}/registration`;
    rightContent = contractIsEnded
      ? undefined
      : renderActions({
          content: `${getLocalizedText(
            "contract_card.checklist.registration.list_action.start",
          )} ›`,
          url: link,
          color: "green",
          size: "small",
        });
  } else {
    onClick = () => {
      if (rentRegistrationId) {
        onShowRentRegistration(rentRegistrationId);
      }
    };
    rightContent = renderActions({
      content: `${getLocalizedText(
        "contract_card.checklist.registration.list_action.detail",
      )} ›`,
      onClick,
      appearance: "link",
    });
  }
  return {
    actionStatus,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedRegistration",
        entityKey: "registration",
        shouldCheck: true,
      });
    },
    title,
    content,
    link,
    onClick,
    rightContent,
  };
};

export const getBrusselsRegistrationMessage = (params: {
  property: IProperty;
  contract: IContract;
  registration?: IRegistration;

  // Callbacks
  onShowRentRegistration: (registrationId: number) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];
}): IChecklistItem | undefined => {
  const { property, contract, onShowRentRegistration, updateContract } = params;

  const {
    rentRegistrationId: id,
    rentRegistrationBrusselsStatus,
    leaseType,
  } = contract;

  const registrationStatus =
    rentRegistrationBrusselsStatus || ERegistrationBrusselsStatus.None;

  if (!hasBrusselsRegistration(property, leaseType)) {
    return;
  }

  const title = getLocalizedText(
    "contract_card.checklist.brussels_registration.heading",
  );

  const link = id
    ? `/properties/${property?.id}/contracts/${contract.id}/registration?id=${id}&documentType=${ERegistrationDocumentType.Brussels}`
    : `/properties/${property.id}/contracts/${contract.id}/registration`;

  const viewDetails = () => id && onShowRentRegistration(id);

  const viewDetailsBtn = renderActions({
    content: `${getLocalizedText(
      "contract_card.checklist.brussels_registration.list_action.detail",
    )} ›`,
    onClick: viewDetails,
    appearance: "link",
  });

  const startRegistrationBtn = renderActions({
    content: `${getLocalizedText(
      "contract_card.checklist.brussels_registration.list_action.start",
    )} ›`,
    url: link,
    color: "green",
    size: "small",
  });

  const onChangeActionStatus = (_: EActionStatus) => {
    handleChangeActionStatus({
      updateContract,
      key: "manuallyCompletedRegistration",
      entityKey: "registration",
      shouldCheck: false,
    });
  };

  const emptyContent = getLocalizedText(
    "contract_card.checklist.brussels_registration.content.empty",
  );

  const completeContent = getLocalizedText(
    "contract_card.checklist.brussels_registration.content.complete",
  );

  const otherContent = getLocalizedText(
    "contract_card.checklist.brussels_registration.content.other",
    {
      brusselsRegStatus: getLocalizedText(
        `brussels_registration.status.${stringToSnakeCase(
          registrationStatus as string,
        )}`,
      ).toLowerCase(),
    },
  );

  if (registrationStatus === ERegistrationBrusselsStatus.RegisteredManually) {
    return {
      title,
      actionStatus: EActionStatus.CompletedManually,
      content: emptyContent,
      rightContent: undefined,
      onChangeActionStatus,
    };
  }

  if (registrationStatus === ERegistrationBrusselsStatus.Registered) {
    return {
      title,
      actionStatus: EActionStatus.CompletedInRentio,
      onClick: viewDetails,
      rightContent: viewDetailsBtn,
      content: completeContent,
      onChangeActionStatus,
    };
  }

  if (registrationStatus === ERegistrationBrusselsStatus.Registering) {
    return {
      title,
      actionStatus: EActionStatus.PendingRentio,
      onClick: viewDetails,
      rightContent: viewDetailsBtn,
      content: otherContent,
      onChangeActionStatus,
    };
  }

  return {
    title,
    link,
    actionStatus: EActionStatus.Incomplete,
    content: emptyContent,
    rightContent: startRegistrationBtn,
    onChangeActionStatus,
  };
};

export const getAvailableTemplatesForType = (params: {
  type: "general" | "contract";
  isBroker: boolean;
  property: IProperty;
  contract?: IContract;
  templates?: ITemplate[];
}) => {
  const { type, isBroker, property, contract, templates } = params;

  if (!templates) {
    return;
  }

  if (type === "contract") {
    return templates.filter(template => {
      // Country
      if (template.country !== (property?.countryId || "BE")) {
        return false;
      }

      // Broker
      if (isBroker && !template.tags.includes(ETemplateTag.Broker)) {
        return false;
      }

      // Consumer
      if (!isBroker && !template.tags.includes(ETemplateTag.Consumer)) {
        return false;
      }

      // Property type
      switch (property?.typeId || -1) {
        // House
        case 2:
        // Apartment
        case 3:
          if (template.type === ETemplateType.ContractResidential) {
            return true;
          }

          if (
            template.type !== ETemplateType.ContractResidentialShort &&
            template.type !== ETemplateType.ContractResidentialLong
          ) {
            return false;
          }

          // Residential duration
          if (contract?.stopDate) {
            const durationInYears = differenceInYears(
              contract.stopDate,
              contract.startDate,
            );

            if (
              template.type === ETemplateType.ContractResidentialShort &&
              durationInYears > 3
            ) {
              return false;
            }

            if (
              template.type === ETemplateType.ContractResidentialLong &&
              durationInYears <= 3
            ) {
              return false;
            }
          }

          break;

        // Student housing
        case 4:
          if (template.type !== ETemplateType.ContractStudentResidence) {
            return false;
          }

          break;

        // Commercial
        case 5:
          if (template.type !== ETemplateType.ContractCommercial) {
            return false;
          }

          break;

        // Parking lot
        case 6:
          if (template.type !== ETemplateType.ContractParkingLot) {
            return false;
          }

          break;

        // Garage
        case 7:
          if (template.type !== ETemplateType.ContractGarage) {
            return false;
          }

          break;
        // NO (8) "other" here, that's the default

        case 9: // Office
        case 10: // Warehouse
        case 11: // Shed
        case 12: // Storage
          if (template.type !== ETemplateType.ContractOther) {
            return false;
          }

          break;

        default:
          break;
      }

      return true;
    });
  }

  return templates.filter(template => {
    // Broker
    if (isBroker && !template.tags.includes(ETemplateTag.Broker)) {
      return false;
    }

    // Consumer
    if (!isBroker && !template.tags.includes(ETemplateTag.Consumer)) {
      return false;
    }

    return [
      ETemplateType.KeyHandover,
      ETemplateType.Other,
      ETemplateType.PropertyInspection,
    ].includes(template.type);
  });
};

export const getRentDepositReleaseMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Callbacks
  onShowRentDeposit: (rentDepositId: number) => void;
  onStartRelease: (rentDepositId: number) => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  rentDeposit?: IRentDeposit;
  isFetchingRentDeposit: boolean;
}): IChecklistItem | undefined => {
  const {
    contract,
    // property,

    // Callbacks
    onShowRentDeposit,
    onStartRelease,

    // Actions
    updateContract,

    // Config
    rentDeposit,
    isFetchingRentDeposit,
  } = params;

  const { manuallyCompletedEndRentDeposit } = contract;

  if (contract.contractType !== EContractType.Basic) {
    return;
  }

  const title = getLocalizedText(
    "property.dashboard.checklist.rent_deposit_release.heading",
  );

  const onChangeActionStatus = (_: EActionStatus) => {
    handleChangeActionStatus({
      updateContract,
      key: "manuallyCompletedEndRentDeposit",
      entityKey: "rent_deposit",
      shouldCheck: !manuallyCompletedEndRentDeposit,
    });
  };

  const actionStatusText = manuallyCompletedEndRentDeposit
    ? getLocalizedText(manuallyCompletedEndRentDeposit)
    : undefined;

  if (!rentDeposit && isFetchingRentDeposit) {
    return {
      actionStatus: EActionStatus.Loading,
      title,
      content: null,
    };
  }

  if (manuallyCompletedEndRentDeposit) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus,
      actionStatusText,
      title,
      content: getLocalizedText(
        "property.dashboard.checklist.rent_deposit.content.established",
        {
          value: rentDeposit?.amount ? formatCurrency(rentDeposit?.amount) : "",
        },
      ),
      rightContent: undefined,
    };
  }

  if (!rentDeposit) {
    return {
      actionStatus: EActionStatus.Incomplete,
      onChangeActionStatus,
      actionStatusText,
      title,
      content: getLocalizedText(
        "property.dashboard.checklist.rent_deposit_release.empty_content",
      ),
      rightContent: null,
    };
  }

  const { id, status, banqupContractStatus, provider } = rentDeposit;

  const viewDetailButton = renderActions({
    content: `${getLocalizedText(
      "property.dashboard.checklist.rent_deposit_release.action",
    )} ›`,
    appearance: "link",
    onClick: () => onShowRentDeposit(id),
  });

  const isMigrationCase =
    rentDeposit.provider === ERentDepositProvider.Migrated;

  const releaseStatuses = [
    ERentDepositStatus.ReleaseNew,
    ERentDepositStatus.WaitingForIBAN,
    ERentDepositStatus.WaitingForValidations,
    ERentDepositStatus.ReleaseToSign,
    ERentDepositStatus.ReleaseSigned,
    ERentDepositStatus.ManualVerification,
    ERentDepositStatus.ManualVerificationJudicial,
    ERentDepositStatus.PayingOut,
  ];

  if (isMigrationCase) {
    releaseStatuses.push(ERentDepositStatus.WaitingForVerificationByBanqup);
  }

  if (releaseStatuses.includes(rentDeposit.status)) {
    return {
      actionStatus: EActionStatus.PendingRentio,
      title,
      content: (
        <Stack alignment="center" spacing="tight">
          <Box>
            {getLocalizedText(
              "property.dashboard.checklist.rent_deposit_release.content.release_in_progress",
            )}
          </Box>
          <Box>
            <Lozenge
              isBold
              key={`${id}-payment-status`}
              appearance={getStatusVariation(status)}
            >
              {ts.followUpRentDepositsFilterStatusLabel({
                extra: { key: status },
              })}
            </Lozenge>
          </Box>
        </Stack>
      ),
      onClick: () => onShowRentDeposit(id),
      rightContent: viewDetailButton,
    };
  }

  const providerIsBanqup = provider !== ERentDepositProvider.MangoPay;

  const releaseFundsTransferred = providerIsBanqup
    ? banqupContractStatus === EBanqupContractStatus.ReleaseFundsTransferred
    : status === ERentDepositStatus.Released;

  if (releaseFundsTransferred) {
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      title,
      content: getLocalizedText(
        `property.dashboard.checklist.rent_deposit_release.${EBanqupContractStatus.ReleaseFundsTransferred.toLowerCase()}.content`,
      ),
      onClick: () => onShowRentDeposit(id),
      rightContent: viewDetailButton,
    };
  }

  return {
    actionStatus: EActionStatus.PendingRentio,
    title,
    link: `/forms/rent-deposit/${id}/release`,
    content: (
      <div>
        {getLocalizedText(
          "property.dashboard.checklist.rent_deposit_release.release_not_initiated",
        )}
      </div>
    ),
    rightContent: renderActions({
      content: `${getLocalizedText(
        "property.dashboard.checklist.rent_deposit_release.not_initiated_action",
      )} ›`,
      onClick: () => onStartRelease(id),
      color: "green",
      size: "small",
    }),
  };
};

export const getOutstandingPaymentOrderMessage = (params: {
  property: IProperty;
  contract: IContract;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  isBroker: boolean;
  paymentOrdersCount?: number;
  isFetchingPaymentOrdersCount: boolean;
}): IChecklistItem | undefined => {
  const {
    property,
    contract,

    // Actions
    updateContract,

    paymentOrdersCount = 0,
    isFetchingPaymentOrdersCount,
  } = params;

  const { manuallyCompletedEndPaymentRent } = contract;

  if (contract.contractType !== EContractType.Basic) {
    return;
  }

  if (isFetchingPaymentOrdersCount) {
    return {
      actionStatus: EActionStatus.Loading,
      title: getLocalizedText(
        "property.dashboard.checklist.outstanding_payment_order.heading",
      ),
      content: null,
    };
  }

  if (manuallyCompletedEndPaymentRent) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndPaymentRent",
          entityKey: "payment_orders",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedEndPaymentRent),
      title: ts.propertyDashboardChecklistPaymentOrderOutstandingHeading(),
      content: (
        <div>{ts.propertyDashboardChecklistPaymentOrderEmptyContent()}</div>
      ),
      rightContent: undefined,
    };
  }

  if (paymentOrdersCount > 0) {
    return {
      actionStatus: EActionStatus.PendingRentio,
      title: getLocalizedText(
        "property.dashboard.checklist.outstanding_payment_order.heading",
      ),
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndPaymentRent",
          entityKey: "payment_orders",
          shouldCheck: true,
        });
      },
      content: (
        <div>
          {getLocalizedText(
            "property.dashboard.checklist.outstanding_payment_order.has_outstanding_payments",
          )}
        </div>
      ),
      link: `/properties/${property.id}/payments`,
      rightContent: renderActions({
        content: `${getLocalizedText("system.view")} ›`,
        appearance: "link",
        url: `/properties/${property.id}/payments`,
      }),
    };
  }

  return {
    actionStatus: EActionStatus.CompletedInRentio,
    title: getLocalizedText(
      "property.dashboard.checklist.outstanding_payment_order.heading",
    ),
    content: (
      <div>
        {getLocalizedText(
          "property.dashboard.checklist.outstanding_payment_order.no_outstanding_payments",
        )}
      </div>
    ),
    rightContent: undefined,
  };
};

export const getOutgoingPropertyInspectionReportMessage = (params: {
  property: IProperty;
  contract: IContract;
  isBroker: boolean;
  broker?: IBroker;

  // Callbacks
  onOrderPropertyInspectionReport: () => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  partnerships?: IPartnership[];
}): IChecklistItem | undefined => {
  const {
    // property,
    contract,

    // Callbacks
    onOrderPropertyInspectionReport,

    // Actions
    updateContract,

    // Config
    partnerships,
  } = params;

  const {
    manuallyCompletedEndPropertyInspectionReport,
    locationDescriptionInRequestedAt,
    locationDescriptionOutRequestedAt,
  } = contract;
  const title = getLocalizedText(
    "contract_card.checklist.outgoing_location_description.heading",
  );

  if (manuallyCompletedEndPropertyInspectionReport) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndPropertyInspectionReport",
          entityKey: "location_description",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(
        manuallyCompletedEndPropertyInspectionReport,
      ),
      title,
      content: (
        <div>
          {getLocalizedText(
            "contract_card.checklist.end_location_description.manually",
          )}
        </div>
      ),
      rightContent: undefined,
    };
  }

  if (
    locationDescriptionInRequestedAt &&
    locationDescriptionOutRequestedAt &&
    locationDescriptionInRequestedAt === locationDescriptionOutRequestedAt
  ) {
    return {
      actionStatus: EActionStatus.Incomplete,
      title: ts.contractCardChecklistLocationDescriptionHeading(),
      content: (
        <div>
          {getLocalizedText(
            "contract_card.checklist.outgoing_location_description.both.content",
            {
              date: formatDate(locationDescriptionOutRequestedAt!),
            },
          )}
        </div>
      ),
      onClick: onOrderPropertyInspectionReport,
      rightContent: renderActions({
        content: `${ts.contractCardChecklistLocationDescriptionAgainAction()} ›`,
        onClick: onOrderPropertyInspectionReport,
        color: "green",
        appearance: "link",
      }),
    };
  }

  if (locationDescriptionOutRequestedAt) {
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      title,
      content: (
        <div>
          {getLocalizedText(
            "contract_card.checklist.outgoing_location_description.content",
            {
              date: formatDate(locationDescriptionOutRequestedAt!),
            },
          )}
        </div>
      ),
      onClick: onOrderPropertyInspectionReport,
      rightContent: renderActions({
        content: `${getLocalizedText(
          "contract_card.checklist.location_description.again_action",
        )} ›`,
        onClick: onOrderPropertyInspectionReport,
        color: "green",
        appearance: "link",
      }),
    };
  }

  // Only hide if feature enabled & there are no partnerships
  if (!partnerships) {
    return undefined;
  }

  let isOnlyMexp = false;
  if (
    (partnerships || []).length === 1 &&
    (partnerships || [])[0].locationDescriptorKey === "MEXP"
  ) {
    isOnlyMexp = true;
  }

  return {
    actionStatus: EActionStatus.PendingRentio,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedEndPropertyInspectionReport",
        entityKey: "location_description",
        shouldCheck: true,
      });
    },
    title,
    content: (
      <div>
        {isOnlyMexp
          ? getLocalizedText(
              "contract_card.checklist.outgoing_location_description.empty_content",
            )
          : getLocalizedText(
              "contract_card.checklist.location_description.empty_content_generic",
            )}
      </div>
    ),
    onClick: onOrderPropertyInspectionReport,
    rightContent: renderActions({
      content: `${ts.contractCardChecklistLocationDescriptionEmptyAction()} ›`,
      onClick: onOrderPropertyInspectionReport,
      color: "green",
      size: "small",
    }),
  };
};

// export const getRentioMoveServiceForExitMessage = (params: {
//   property: IProperty;
//   contract: IContract;
//   hasRentioMoveServiceForBroker: boolean;
//   // Callbacks
//   onShowMoveRequest: (moveRequestId: number) => void;
//   onCreateRentioMoveRequest: () => void;

//   // Actions
//   updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

//   // Config
//   isBroker: boolean;
//   internalModeEnabled: boolean;
// }): IChecklistItem | undefined => {
//   const {
//     contract,

//     // Callbacks
//     onCreateRentioMoveRequest,

//     // Actions
//     updateContract,
//     // Config
//     isBroker,
//     hasRentioMoveServiceForBroker,
//     internalModeEnabled,
//   } = params;
//   const { rentioMoveStatus, manuallyCompletedEndMove } = contract;

//   const contractIsEnded = isEndedContract(contract);

//   if (isBroker && !hasRentioMoveServiceForBroker && !internalModeEnabled) {
//     return {
//       actionStatus: manuallyCompletedEndMove
//         ? EActionStatus.CompletedManually
//         : EActionStatus.Incomplete,
//       onChangeActionStatus: (_: EActionStatus) => {
//         handleChangeActionStatus({
//           updateContract,
//           key: "manuallyCompletedEndMove",
//           entityKey: "rentio_move_service",
//           shouldCheck: !manuallyCompletedEndMove,
//         });
//       },
//       actionStatusText: manuallyCompletedEndMove
//         ? getLocalizedText(manuallyCompletedEndMove)
//         : undefined,
//       title: getLocalizedText(
//         "contract_card.checklist.rentio_move_service.heading.manual",
//       ),
//       content: getLocalizedText(
//         "contract_card.checklist.rentio_move_service.content.manual",
//       ),
//       rightContent: undefined,
//     };
//   }

//   let title = getLocalizedText(
//     "contract_card.checklist.rentio_move_service.heading",
//   );
//   let content = getLocalizedText(
//     `contract_card.checklist.rentio_move_service.content.${
//       rentioMoveStatus || ERentioMoveServiceStatus.None
//     }`.toLowerCase(),
//   );

//   if (manuallyCompletedEndMove) {
//     return {
//       actionStatus: EActionStatus.CompletedManually,
//       onChangeActionStatus: (_: EActionStatus) => {
//         handleChangeActionStatus({
//           updateContract,
//           key: "manuallyCompletedEndMove",
//           entityKey: "rentio_move_service",
//           shouldCheck: false,
//         });
//       },
//       actionStatusText: getLocalizedText(manuallyCompletedEndMove),
//       title,
//       content,
//       rightContent: undefined,
//     };
//   }

//   if (contract.rentioMoveStatus === ERentioMoveServiceStatus.Processing) {
//     const rentioMoveDashboardLink = getRentioMoveUrl(contract.id);
//     return {
//       actionStatus: EActionStatus.PendingRentio,
//       onChangeActionStatus: (_: EActionStatus) => {
//         handleChangeActionStatus({
//           updateContract,
//           key: "manuallyCompletedEndMove",
//           entityKey: "rentio_move_service",
//           shouldCheck: true,
//         });
//       },
//       title,
//       content,
//       onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
//       rightContent: renderActions({
//         content: `${getLocalizedText(
//           "contract_card.checklist.rentio_move_service.list_action.detail",
//         )} ›`,
//         onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
//         appearance: "link",
//       }),
//     };
//   }

//   if (contract.rentioMoveStatus === ERentioMoveServiceStatus.Sent) {
//     const rentioMoveDashboardLink = getRentioMoveUrl(contract.id);
//     return {
//       actionStatus: EActionStatus.CompletedInRentio,
//       onChangeActionStatus: (_: EActionStatus) => {
//         handleChangeActionStatus({
//           updateContract,
//           key: "manuallyCompletedEndMove",
//           entityKey: "rentio_move_service",
//           shouldCheck: true,
//         });
//       },
//       title,
//       content,
//       onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
//       rightContent: renderActions({
//         content: `${getLocalizedText(
//           "contract_card.checklist.rentio_move_service.list_action.detail",
//         )} ›`,
//         onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
//         appearance: "link",
//       }),
//     };
//   }

//   return {
//     actionStatus: EActionStatus.Incomplete,
//     onChangeActionStatus: (_: EActionStatus) => {
//       handleChangeActionStatus({
//         updateContract,
//         key: "manuallyCompletedEndMove",
//         entityKey: "rentio_move_service",
//         shouldCheck: true,
//       });
//     },
//     title,
//     content,
//     onClick: contractIsEnded ? undefined : onCreateRentioMoveRequest,
//     rightContent: contractIsEnded
//       ? undefined
//       : renderActions({
//           content: `${getLocalizedText(
//             "contract_card.checklist.rentio_move_service.list_action.start",
//           )} ›`,
//           onClick: onCreateRentioMoveRequest,
//           color: "green",
//           size: "small",
//         }),
//   };
// };

export const getRentioMoveEndServiceMessage = (params: {
  property: IProperty;
  contract: IContract;
  hasRentioMoveServiceForBroker: boolean;
  // Callbacks
  onCreateRentioMoveRequest: () => void;

  // Actions
  updateContract: ReturnType<typeof contractHooks.useUpdate>["update"];

  // Config
  isBroker: boolean;
  internalModeEnabled: boolean;
}): IChecklistItem | undefined => {
  const {
    contract,

    // Callbacks
    onCreateRentioMoveRequest,

    // Actions
    updateContract,
    // Config
    isBroker,
    hasRentioMoveServiceForBroker,
    internalModeEnabled,
  } = params;
  const { rentioMoveExitStatus, manuallyCompletedEndMove } = contract;

  const contractIsEnded = isEndedContract(contract);

  if (isBroker && !hasRentioMoveServiceForBroker && !internalModeEnabled) {
    return {
      actionStatus: manuallyCompletedEndMove
        ? EActionStatus.CompletedManually
        : EActionStatus.Incomplete,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndMove",
          entityKey: "rentio_move_end_service",
          shouldCheck: !manuallyCompletedEndMove,
        });
      },
      actionStatusText: manuallyCompletedEndMove
        ? getLocalizedText(manuallyCompletedEndMove)
        : undefined,
      title: getLocalizedText(
        "contract_card.checklist.rentio_move_end_service.heading.manual",
      ),
      content: getLocalizedText(
        "contract_card.checklist.rentio_move_end_service.content.manual",
      ),
      rightContent: undefined,
    };
  }

  const title = getLocalizedText(
    "contract_card.checklist.rentio_move_end_service.heading",
  );

  if (manuallyCompletedEndMove) {
    return {
      actionStatus: EActionStatus.CompletedManually,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndMove",
          entityKey: "rentio_move_end_service",
          shouldCheck: false,
        });
      },
      actionStatusText: getLocalizedText(manuallyCompletedEndMove),
      title,
      content: getLocalizedText(
        "contract_card.checklist.rentio_move_end_service.content.manual",
      ),
      rightContent: undefined,
    };
  }

  // Processing state, contract.rentioMoveExitStatus = "PROCESSING"
  if (rentioMoveExitStatus === ERentioMoveServiceStatus.Processing) {
    const rentioMoveDashboardLink = getRentioMoveUrl(contract.id);
    return {
      actionStatus: EActionStatus.PendingRentio,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndMove",
          entityKey: "rentio_move_end_service",
          shouldCheck: true,
        });
      },
      title,
      content: getLocalizedText(
        "contract_card.checklist.rentio_move_end_service.content.processing",
      ),
      onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
      rightContent: renderActions({
        content: `${getLocalizedText(
          "contract_card.checklist.rentio_move_service.list_action.detail",
        )} ›`,
        onClick: () => window.open(rentioMoveDashboardLink, "_blank"),
        appearance: "link",
      }),
    };
  }

  // Completed state, contract.rentioMoveExitStatus = "SENT"
  if (rentioMoveExitStatus === ERentioMoveServiceStatus.Sent) {
    return {
      actionStatus: EActionStatus.CompletedInRentio,
      onChangeActionStatus: (_: EActionStatus) => {
        handleChangeActionStatus({
          updateContract,
          key: "manuallyCompletedEndMove",
          entityKey: "rentio_move_end_service",
          shouldCheck: true,
        });
      },
      title,
      content: getLocalizedText(
        "contract_card.checklist.rentio_move_end_service.content.sent",
      ),
    };
  }

  // Default - not started
  return {
    actionStatus: EActionStatus.Incomplete,
    onChangeActionStatus: (_: EActionStatus) => {
      handleChangeActionStatus({
        updateContract,
        key: "manuallyCompletedEndMove",
        entityKey: "rentio_move_end_service",
        shouldCheck: true,
      });
    },
    title,
    content: getLocalizedText(
      "contract_card.checklist.rentio_move_end_service.content.none",
    ),
    onClick: contractIsEnded ? undefined : onCreateRentioMoveRequest,
    rightContent: contractIsEnded
      ? undefined
      : renderActions({
          content: `${getLocalizedText(
            "contract_card.checklist.rentio_move_service.list_action.start",
          )} ›`,
          onClick: onCreateRentioMoveRequest,
          color: "green",
          size: "small",
        }),
  };
};

export const getPaymentMethodFromRentDepositPo = (
  rentDeposit: IRentDeposit,
) => {
  if (!rentDeposit.paymentOrder) {
    return;
  }

  if (!rentDeposit.paymentOrder.paymentRequests?.length) {
    return;
  }

  const paidPayout = rentDeposit.paymentOrder.paymentRequests.find(
    paymentRequest => paymentRequest.payoutType === EPayoutType.Charge,
  );

  return paidPayout?.paymentMethod;
};
